import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { publishAnnotation, setAnnotationStatus, unpublishAnnotation, countLints } from '@/api/annotations'
import { keys } from '@/api/keys'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import { isStageBody, useEditorStore } from '@/stores/editorStore'
import type { ReviewStatus } from '@/schema'

import { describeSaveError, ensureSaved } from './useAutosave'

/**
 * `draft → reviewed → published` and back (§5.3).
 *
 * The two forward moves are refused by the server while there are error
 * lints, and the refusal carries the fix hints — so this component never
 * pre-judges: it asks, and turns a `422` into the sentence the server sent
 * rather than a generic failure.
 *
 * These act on the **saved** copy. With unsaved changes on screen the pill first asks "Save your
 * changes and …?"; if that save fails, nothing is published — the old copy is never put into
 * stories behind the person's back.
 */

const LABEL: Record<ReviewStatus, string> = {
  seed: 'From the file',
  proposed: 'Suggested',
  draft: 'Draft',
  reviewed: 'Checked',
  published: 'In use',
}

const TONE: Record<ReviewStatus, 'neutral' | 'info' | 'warn' | 'ok' | 'reviewed'> = {
  seed: 'neutral',
  proposed: 'info',
  draft: 'warn',
  reviewed: 'reviewed',
  published: 'ok',
}

type Action = 'reviewed' | 'draft' | 'publish' | 'unpublish'

/** What the action does, finishing "Save your changes and …". */
const VERB: Record<Action, string> = {
  draft: 'put it back to draft',
  reviewed: 'mark it as checked',
  publish: 'use it in stories',
  unpublish: 'stop using it in stories',
}

const BUTTON: Record<Action, string> = {
  draft: 'Save and put back to draft',
  reviewed: 'Save and mark as checked',
  publish: 'Save and use in stories',
  unpublish: 'Save and stop using it',
}

export interface ReviewStatusPillProps {
  className?: string
}

export function ReviewStatusPill({ className }: ReviewStatusPillProps) {
  const qc = useQueryClient()
  const meta = useEditorStore((s) => s.meta)
  const doc = useEditorStore((s) => s.doc)
  const applyServerMeta = useEditorStore((s) => s.applyServerMeta)
  const [confirm, setConfirm] = useState<Action | null>(null)

  const errors = isStageBody(doc) ? countLints(doc.lints).errors : 0

  const run = useMutation({
    mutationFn: async (action: Action) => {
      if (!meta) throw new Error('Nothing is open.')
      const st = useEditorStore.getState()
      if (st.dirty || st.saving) {
        const saved = await ensureSaved(qc)
        if (!saved) {
          const why = describeSaveError(useEditorStore.getState().saveError)
          throw new Error(`Your changes could not be saved, so nothing else was changed.${why ? ` ${why}` : ''}`)
        }
      }
      switch (action) {
        case 'publish':
          return publishAnnotation(meta.uid)
        case 'unpublish':
          return unpublishAnnotation(meta.uid)
        default:
          return setAnnotationStatus(meta.uid, action)
      }
    },
    onSuccess: (env) => {
      applyServerMeta(env)
      void qc.invalidateQueries({ queryKey: keys.assets.all() })
      if (meta) void qc.invalidateQueries({ queryKey: keys.annotation.doc(meta.uid) })
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; message?: string; details?: { lints?: { message: string; fix_hint?: string }[] } }
      const first = e.details?.lints?.[0]
      toast.error(first ? first.message : (e.message ?? 'That did not work.'), first?.fix_hint ? { description: first.fix_hint } : {})
    },
  })

  if (!meta) return null

  const request = (action: Action) => {
    const st = useEditorStore.getState()
    if (st.dirty) setConfirm(action)
    else run.mutate(action)
  }

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label={`Status: ${LABEL[meta.status]}. Change it.`}>
            <Badge tone={TONE[meta.status]} dot>
              {LABEL[meta.status]}
            </Badge>
            {meta.published_tier === 'auto' ? <Badge tone="auto">Auto</Badge> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={run.isPending || meta.status === 'draft'} onSelect={() => request('draft')}>
            Put back to draft
          </DropdownMenuItem>
          <DropdownMenuItem disabled={run.isPending || errors > 0} onSelect={() => request('reviewed')}>
            Mark as checked{errors > 0 ? ` — ${errors} thing${errors === 1 ? '' : 's'} to sort out first` : ''}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={run.isPending || errors > 0} onSelect={() => request('publish')}>
            Use this in stories
          </DropdownMenuItem>
          {meta.published_rev !== null ? (
            <DropdownMenuItem disabled={run.isPending} onSelect={() => request('unpublish')}>
              Stop using it in stories
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={confirm !== null} onOpenChange={(open) => (!open ? setConfirm(null) : undefined)}>
        <DialogContent size="sm" hideClose>
          <DialogHeader>
            <DialogTitle>Save your changes first?</DialogTitle>
            <DialogDescription>
              {confirm ? `You have unsaved changes. They will be saved, and then the studio will ${VERB[confirm]}.` : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              autoFocus
              onClick={() => {
                const action = confirm
                setConfirm(null)
                if (action) run.mutate(action)
              }}
            >
              {confirm ? BUTTON[confirm] : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
